import { useMutation, useQueryClient } from '@tanstack/react-query'
import { API_BASE } from '../data/constants.js'

/**
 * useManualContributor — add/remove manual contributors on a synced
 * sub-goal. POST /goals/subgoal/contributors/manual handles both
 * actions; this hook exposes them as op.add(...) and op.remove(...).
 *
 * Manual contributors live on `sub.manualContributors`, are preserved
 * by syncRevenueFromHubspot, and their amounts roll into sub.current.
 */
export function useManualContributor() {
  const qc = useQueryClient()

  const post = async (body) => {
    const res = await fetch(API_BASE + '/goals/subgoal/contributors/manual', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    if (!res.ok) {
      let msg = `HTTP ${res.status}`
      try {
        const j = await res.json()
        if (j?.error) msg = j.error
      } catch {}
      throw new Error(msg)
    }
    return res.json()
  }

  const invalidate = () => qc.invalidateQueries({ queryKey: ['goals'] })

  const addMutation = useMutation({
    mutationFn: (body) => post({ ...body, action: 'add' }),
    onSuccess: invalidate,
  })

  const removeMutation = useMutation({
    mutationFn: (body) => post({ ...body, action: 'remove' }),
    onSuccess: invalidate,
  })

  return {
    add: addMutation.mutate,
    remove: removeMutation.mutate,
    isAdding: addMutation.isPending,
    isRemoving: removeMutation.isPending,
    error: addMutation.error || removeMutation.error,
  }
}
